/* eslint-disable complexity */
/* global URL_MENU */
import { FC, useRef, useState, useEffect } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import logoPlick from '@assets/images/logo.svg';
import img_facebook from '@assets/images/facebook.svg';
import img_instagram from '@assets/images/instagram.svg';
import IconoQ from '@assets/images/icono-question.svg';
import priceIcon from '@assets/images/priceIcon.svg';
import { useAuth } from '@context/AuthContext';
import { useDesktop } from '@utils/hooks/useDesktop';
import useModules from '@utils/hooks/useModules';
import { useModuleGate } from '@utils/hooks/useModuleGate';
import ButtonWithIconLeft from '@components/interface/ButtonWithIconLeft';
import ButtonAction from '@components/interface/ButtonAction';
import { useNavigate } from 'react-router-dom';
import { useEditCommerce, useGetCommerce } from '@pages/Commerce/hooks/commerce';
import BtnQR from '@assets/images/iconos-aside-panel-qr.svg';
import onlinePaymentIcon from '@assets/images/onlinePaymentIcon.svg';
import UsersIcon from '@assets/images/icono-mis clientes.svg';
import NavBarStatus from '../NavBar/components/interface/NavBarStatus';
import { ModuleName } from '@utils/constants';
import { ModuleKey } from '@interfaces/Permissions';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { OrdersCounter } from './OrdersCounter';
import { NewsFeed } from './components/NewsFeed';
import {
  IconBulb,
  IconHelpCircle,
  IconBrandWhatsapp,
  IconChefHat,
  IconUser,
  IconCloudCheck,
  IconLogout,
  IconDotsVertical,
  IconMessage2Filled,
} from '@tabler/icons-react';
import FounderContactModal from '@components/interface/FounderContactModal';
import FounderMessageForm from '@components/interface/FounderMessageForm';
import { openExternalUrl } from '@utils/navigationExternalUrl';
import { EXTERNAL_URLS } from '@utils/constants';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';

const getNotIncludedOnCurrentPlanIcon = (hasModule: boolean) => {
  return hasModule
    ? null
    : {
        icon: 'solar:tag-price-bold',
        color: 'white',
        width: '20px',
        containerClassName: 'absolute right-0',
      };
};

const getNavigatePath = (hasModule: boolean, path: string) => {
  return hasModule ? path : 'suscripcion/' + path;
};

const Header: FC = () => {
  const auth = useAuth();
  const { isFreemium, isFounder, orderLimits } = useSubscription();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);
  const isDesktop = useDesktop();
  const navigate = useNavigate();
  const { commerce, isFetching } = useGetCommerce();
  const { handleUpdate } = useEditCommerce();
  const { modulePermissions } = useModules();
  const { moduleGate } = useModuleGate();
  // Tri-state resolver for nav-item gating: `moduleGate` returns 'loading' while
  // modules are still fetching. Treating 'loading' as "not denied" (instead of
  // collapsing it to `false` like the old `hasModule(x) || false`) avoids painting
  // the upsell lock / hiding the entry while we still don't know the real answer —
  // it only shows the locked/upsell state once we've actually confirmed denial.
  const hasModuleAccess = (module: ModuleKey) => moduleGate(module) !== 'denied';
  const permissions = modulePermissions(ModuleName.HORARIOS);
  const [isNewMenuOpen, setIsNewMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const { trackEvent } = useTracking();
  const [showFounderWarning, setShowFounderWarning] = useState(false);
  const [showFounderForm, setShowFounderForm] = useState(false);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setIsNewMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const switchOpen = () => {
    if (isOpen) {
      if (mobileMenuRef.current) {
        mobileMenuRef.current.classList.remove('translate-y-16');
        mobileMenuRef.current.classList.add('-translate-y-60');
        mobileMenuRef.current.addEventListener('transitionend', () => setOpen(false), { once: true });
      }
    } else setOpen(true);
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      if (mobileMenuRef && mobileMenuRef.current) {
        if (isOpen) {
          mobileMenuRef.current.classList.remove('-translate-y-60');
          mobileMenuRef.current.classList.add('translate-y-16');
        }
      }
    });
  }, [isOpen]);

  const newMenuItems = [
    {
      label: 'Sugerencias',
      icon: <IconBulb width={22} height={22} />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.SuggestionsClicked);
        openExternalUrl(EXTERNAL_URLS.IDEAS);
      },
      color: 'inherit',
    },
    {
      label: 'Centro de Ayuda',
      icon: <IconHelpCircle width={22} height={22} />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.SupportClicked);
        openExternalUrl(EXTERNAL_URLS.HELP_CENTER);
      },
      color: 'inherit',
    },
    {
      label: 'Soporte',
      icon: <IconBrandWhatsapp width={22} height={22} />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.WhatsAppSupportClicked);
        openExternalUrl(EXTERNAL_URLS.SUPPORT_WHATSAPP);
      },
      color: 'inherit',
    },
    {
      label: 'Ir a mi menú',
      icon: <IconChefHat width={22} height={22} />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.MenuViewClicked);
        window.open(`${URL_MENU}/${commerce.id_path}`);
      },
      color: 'inherit',
    },
    ...(!isFounder
      ? [
          {
            label: 'Mi cuenta',
            icon: <IconUser width={22} height={22} />,
            onClick: () => {
              trackEvent(TRACK_EVENT_NAMES.MyAccountClicked);
              navigate('mi-cuenta');
            },
            color: 'inherit',
          },
        ]
      : []),
    {
      label: 'Actualizaciones',
      icon: <IconCloudCheck width={22} height={22} />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.UpdatesClicked);
        openExternalUrl(EXTERNAL_URLS.BLOG_UPDATES);
      },
      color: 'inherit',
    },
    {
      label: 'Cerrar Sesión',
      icon: <IconLogout width={22} height={22} />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.LogoutClicked);
        auth.logout();
      },
      color: '#FF3939',
    },
  ];

  return (
    <>
      {!isDesktop && isOpen && (
        <div
          ref={mobileMenuRef}
          className="fixed w-full -translate-y-60 bg-color-primary text-color-white px-2 transition-transform ease-out z-50"
        >
          <div className="flex text-sm justify-center">
            <span>
              <b>Usuario:</b> {auth.user.username}
            </span>
          </div>
          <div className="px-2" onClick={switchOpen}>
            <section className="px-2 justify-center flex flex-col">
              <h5 className="text-lg text-color-white lg:text-color-primary px-2 text-center line-clamp-2">
                {commerce.nombre}
              </h5>
              <NavBarStatus isOpen={commerce.servicio_activo ?? true} />
              <ButtonAction
                colorTxt="color-secondary"
                colorBg="color-white"
                hoverBg="color-gray_light"
                hasBorder
                textButton="SOPORTE"
                icon="clarity:chat-bubble-solid"
                onClick={() => {
                  trackEvent(TRACK_EVENT_NAMES.WhatsAppSupportClicked);
                  openExternalUrl(EXTERNAL_URLS.SUPPORT_WHATSAPP);
                }}
              />
              <ButtonAction
                colorTxt="color-secondary"
                colorBg="color-white"
                hoverBg="color-gray_light"
                hasBorder
                textButton="IR A MI MENÚ"
                icon="fa-solid:hamburger"
                onClick={() => window.open(`${URL_MENU}/${commerce.id_path}`)}
              />
              {permissions.edit && (
                <ButtonAction
                  textButton={commerce.servicio_activo ? 'CERRAR TEMPORALMENTE' : 'ABRIR NEGOCIO'}
                  disable={isFetching}
                  icon={isFetching ? 'eos-icons:loading' : ''}
                  colorBg="color-white"
                  colorTxt="color-secondary"
                  hasBorder
                  hoverBg="color-gray_light"
                  onClick={() => handleUpdate({ ...commerce, servicio_activo: !commerce.servicio_activo })}
                />
              )}
            </section>
            {!isFounder && (
              <ButtonWithIconLeft
                fullWidth
                allowPropagation
                label="Mi cuenta"
                icon="ion:cart"
                iconColor="#4298b5"
                onClick={() => {
                  navigate('mi-cuenta');
                }}
              />
            )}
            {(isFreemium || hasModuleAccess(ModuleName.COMPARTIR)) && (
              <ButtonWithIconLeft
                fullWidth
                allowPropagation
                label="Compartir Menú"
                svgIcon={BtnQR}
                iconColor="#4298b5"
                onClick={() => {
                  navigate(getNavigatePath(hasModuleAccess(ModuleName.COMPARTIR), 'compartir-menu'));
                }}
                rightIcon={getNotIncludedOnCurrentPlanIcon(hasModuleAccess(ModuleName.COMPARTIR))}
              />
            )}
            {(isFreemium || hasModuleAccess(ModuleName.COBROS_EN_LINEA)) && !isFetching && commerce?.business_id && (
              <ButtonWithIconLeft
                fullWidth
                allowPropagation
                label="Cobros en línea"
                svgIcon={onlinePaymentIcon}
                iconColor="#4298b5"
                onClick={() => {
                  navigate(getNavigatePath(hasModuleAccess(ModuleName.COBROS_EN_LINEA), 'pedidos-en-linea'));
                }}
                rightIcon={getNotIncludedOnCurrentPlanIcon(hasModuleAccess(ModuleName.COBROS_EN_LINEA))}
              />
            )}
            {(isFreemium || hasModuleAccess(ModuleName.DATOS_COMERCIO)) && (
              <ButtonWithIconLeft
                fullWidth
                allowPropagation
                label="Datos del comercio"
                icon="fa-solid:store"
                iconColor="#4298b5"
                onClick={() => {
                  navigate(getNavigatePath(hasModuleAccess(ModuleName.DATOS_COMERCIO), 'datos-del-comercio'));
                }}
                rightIcon={getNotIncludedOnCurrentPlanIcon(hasModuleAccess(ModuleName.DATOS_COMERCIO))}
              />
            )}
            {(isFreemium || hasModuleAccess(ModuleName.HORARIOS)) && (
              <ButtonWithIconLeft
                fullWidth
                allowPropagation
                label="Horarios"
                icon="bxs:time-five"
                iconColor="#4298b5"
                onClick={() => {
                  navigate(getNavigatePath(hasModuleAccess(ModuleName.HORARIOS), 'horarios'));
                }}
                rightIcon={getNotIncludedOnCurrentPlanIcon(hasModuleAccess(ModuleName.HORARIOS))}
              />
            )}
            {(isFreemium || hasModuleAccess(ModuleName.TARIFAS)) && (
              <ButtonWithIconLeft
                fullWidth
                allowPropagation
                label="Tarifas y entregas"
                svgIcon={priceIcon}
                iconColor="#4298b5"
                onClick={() => {
                  navigate(getNavigatePath(hasModuleAccess(ModuleName.TARIFAS), 'tarifas'));
                }}
                rightIcon={getNotIncludedOnCurrentPlanIcon(hasModuleAccess(ModuleName.TARIFAS))}
              />
            )}
            {(isFreemium || hasModuleAccess(ModuleName.CUPONES)) && (
              <ButtonWithIconLeft
                fullWidth
                allowPropagation
                label="Cupones"
                icon="mdi:coupon"
                iconColor="#4298b5"
                onClick={() => {
                  navigate(getNavigatePath(hasModuleAccess(ModuleName.CUPONES), 'cupones'));
                }}
                rightIcon={getNotIncludedOnCurrentPlanIcon(hasModuleAccess(ModuleName.CUPONES))}
              />
            )}
            {(isFreemium || hasModuleAccess(ModuleName.USUARIOS)) && (
              <ButtonWithIconLeft
                fullWidth
                allowPropagation
                label="Alta de usuarios"
                svgIcon={UsersIcon}
                iconColor="#4298b5"
                onClick={() => {
                  navigate(getNavigatePath(hasModuleAccess(ModuleName.USUARIOS), 'usuarios'));
                }}
                rightIcon={getNotIncludedOnCurrentPlanIcon(hasModuleAccess(ModuleName.USUARIOS))}
              />
            )}
            {(isFreemium || hasModuleAccess(ModuleName.ASISTENCIA_TECNICA)) && (
              <ButtonWithIconLeft
                fullWidth
                allowPropagation
                label="Asistencia Técnica"
                svgIcon={IconoQ}
                iconColor="#4298b5"
                onClick={() => {
                  navigate(getNavigatePath(hasModuleAccess(ModuleName.ASISTENCIA_TECNICA), 'asistencia'));
                }}
                rightIcon={getNotIncludedOnCurrentPlanIcon(hasModuleAccess(ModuleName.ASISTENCIA_TECNICA))}
              />
            )}

            {(isFreemium || hasModuleAccess(ModuleName.AJUSTES)) && (
              <ButtonWithIconLeft
                fullWidth
                allowPropagation
                label="Ajustes"
                icon="fa6-solid:gear"
                iconColor="#4298b5"
                onClick={() => {
                  navigate(getNavigatePath(hasModuleAccess(ModuleName.AJUSTES), 'ajustes'));
                }}
                rightIcon={getNotIncludedOnCurrentPlanIcon(hasModuleAccess(ModuleName.AJUSTES))}
              />
            )}
            <div className="flex w-full justify-center">
              <ButtonAction
                colorBg="color-warning"
                colorTxt="color-white"
                textButton="Cerrar Sesión"
                onClick={auth.logout}
              />
            </div>
          </div>
        </div>
      )}
      <div className="fixed w-full z-50 bg-color-primary flex justify-between text-color-white h-16 items-center p-4 lg:px-8">
        <div className="flex items-baseline">
          <img src={logoPlick} alt="logo plick" height="25px" />
          {isDesktop && (
            <>
              <span className="ml-2 mr-4">
                Pedidos a un <strong className="bg-color-secondary rounded-sm px-1">click.</strong>
              </span>
              <a
                className="mx-2"
                href={EXTERNAL_URLS.FACEBOOK}
                onClick={(e) => {
                  e.preventDefault();
                  openExternalUrl(EXTERNAL_URLS.FACEBOOK);
                }}
              >
                <img src={img_facebook} alt="Facebook" height="25px" />
              </a>
              <a
                className="mx-2"
                href={EXTERNAL_URLS.INSTAGRAM}
                onClick={(e) => {
                  e.preventDefault();
                  openExternalUrl(EXTERNAL_URLS.INSTAGRAM);
                }}
              >
                <img src={img_instagram} alt="Instagram" height="25px" />
              </a>
            </>
          )}
        </div>
        <div className="flex gap-4">
          <div className="flex items-center">
            <Tippy content="Ayúdanos a mejorar">
              <button
                className="text-white mr-4 hover:text-gray-200 transition-colors"
                onClick={() => {
                  trackEvent(TRACK_EVENT_NAMES.FeedbackButtonClicked);
                  setShowFounderWarning(true);
                }}
              >
                <IconMessage2Filled width={24} height={24} />
              </button>
            </Tippy>
          </div>
          <div className="flex gap-6">
            <NewsFeed />
            {orderLimits?.currentMonthCount !== undefined &&
            orderLimits?.currentMonthCount !== null &&
            orderLimits?.monthlyLimit !== undefined &&
            orderLimits?.monthlyLimit !== null &&
            orderLimits?.quotaUsagePercent !== undefined &&
            orderLimits?.quotaUsagePercent !== null ? (
              <OrdersCounter
                isDesktop={isDesktop}
                current={orderLimits.currentMonthCount}
                limit={orderLimits.monthlyLimit}
                usagePercent={orderLimits.quotaUsagePercent < 100 ? orderLimits.quotaUsagePercent : 100}
              />
            ) : null}

            {!isDesktop && auth.isReady && (
              <button className="w-7 h-full flex flex-col justify-around" onClick={switchOpen}>
                <div className="h-1 w-full bg-color-white rounded-full"></div>
                <div className="h-1 w-full bg-color-white rounded-full"></div>
                <div className="h-1 w-full bg-color-white rounded-full"></div>
              </button>
            )}
          </div>
          {isDesktop ? (
            <div className="relative" ref={buttonRef}>
              <button
                className="outline-none bg-color-white w-[41px] h-[41px] rounded-[6px] text-color-primary flex items-center justify-center"
                onClick={() => setIsNewMenuOpen((prev) => !prev)}
              >
                <IconDotsVertical width={22} height={22} />
              </button>
              {isNewMenuOpen ? (
                <ul
                  className="absolute bg-color-white rounded-[6px] text-color-primary right-0 w-max"
                  style={{ boxShadow: '0px 1px 4px 0px #00000066' }}
                >
                  {newMenuItems.map((item, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-2 py-[8px] hover:bg-color-gray_light px-[10px] rounded-[6px] hover:cursor-pointer"
                      onClick={() => {
                        setIsNewMenuOpen(false);
                        item.onClick();
                      }}
                      style={{ color: item.color }}
                    >
                      {item.icon}
                      <span className="flex items-center">{item.label}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      {showFounderWarning ? (
        <FounderContactModal
          onClose={() => setShowFounderWarning(false)}
          onContinue={() => {
            setShowFounderWarning(false);
            setShowFounderForm(true);
          }}
        />
      ) : null}
      {showFounderForm ? <FounderMessageForm commerce={commerce} onClose={() => setShowFounderForm(false)} /> : null}
    </>
  );
};

export default Header;
